import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import apiService from '../services/api';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  CloudArrowUpIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const SystemAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    overview: {
      totalCompanies: 0,
      totalUsers: 0,
      totalRevenue: 0,
      totalStorage: 0,
      monthlyGrowth: 0,
      storageUsage: 0
    },
    companies: [],
    revenueData: [],
    storageData: [],
    userActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, 1y

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/analytics/system?timeRange=${timeRange}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Mock data for demo
      setAnalytics({
        overview: {
          totalCompanies: 45,
          totalUsers: 1250,
          totalRevenue: 18500,
          totalStorage: 125.6,
          monthlyGrowth: 12.5,
          storageUsage: 68.3
        },
        companies: [
          { name: 'TechCorp Inc', plan: 'enterprise', users: 156, revenue: 2988, status: 'active' },
          { name: 'StartupXYZ', plan: 'pro', users: 45, revenue: 495, status: 'active' },
          { name: 'Global Solutions', plan: 'enterprise', users: 203, revenue: 2988, status: 'active' },
          { name: 'Innovation Labs', plan: 'basic', users: 12, revenue: 348, status: 'trial' },
          { name: 'Digital Agency', plan: 'pro', users: 78, revenue: 792, status: 'active' }
        ],
        revenueData: [
          { month: 'Jan', revenue: 15200 },
          { month: 'Feb', revenue: 16800 },
          { month: 'Mar', revenue: 18500 },
          { month: 'Apr', revenue: 17900 },
          { month: 'May', revenue: 19200 },
          { month: 'Jun', revenue: 21500 }
        ],
        storageData: [
          { category: 'Documents', usage: 45.2, color: 'bg-blue-500' },
          { category: 'Images', usage: 32.1, color: 'bg-green-500' },
          { category: 'Videos', usage: 18.7, color: 'bg-yellow-500' },
          { category: 'Other', usage: 4.0, color: 'bg-gray-500' }
        ],
        userActivity: [
          { action: 'New company registration', company: 'Digital Agency', time: '2 hours ago', type: 'success' },
          { action: 'Storage limit reached', company: 'StartupXYZ', time: '4 hours ago', type: 'warning' },
          { action: 'Plan upgraded to Enterprise', company: 'TechCorp Inc', time: '1 day ago', type: 'success' },
          { action: 'Payment failed', company: 'Innovation Labs', time: '2 days ago', type: 'error' },
          { action: 'New user added', company: 'Global Solutions', time: '3 days ago', type: 'info' }
        ]
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Analytics</h1>
              <p className="text-gray-600 mt-2">
                Monitor system performance, revenue trends, and user activity across all companies.
              </p>
            </div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="block w-48 rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
        </motion.div>

      {/* Overview Stats */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Companies</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {analytics.overview.totalCompanies}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      analytics.overview.monthlyGrowth >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {analytics.overview.monthlyGrowth >= 0 ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                      )}
                      {formatPercentage(analytics.overview.monthlyGrowth)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UsersIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {analytics.overview.totalUsers.toLocaleString()}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Monthly Revenue</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(analytics.overview.totalRevenue)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CloudArrowUpIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Storage Used</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {analytics.overview.totalStorage} GB
                    </div>
                    <div className="ml-2 text-sm text-gray-500">
                      ({analytics.overview.storageUsage}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts and Data */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Revenue Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Revenue Trend</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {analytics.revenueData.map((item, index) => (
                <div key={item.month} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 text-sm font-medium text-gray-700">{item.month}</div>
                  </div>
                  <div className="flex items-center flex-1 mx-4">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(item.revenue / 25000) * 100}%` }}
                        transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                        className="bg-blue-500 h-3 rounded-full"
                      />
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-900 w-20 text-right">
                    {formatCurrency(item.revenue)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Storage Usage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Storage Usage by Type</h3>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {analytics.storageData.map((item, index) => (
                <div key={item.category}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{item.category}</span>
                    <span className="text-sm text-gray-500">{item.usage} GB</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(item.usage / 50) * 100}%` }}
                      transition={{ delay: 0.6 + index * 0.1, duration: 0.8 }}
                      className={`h-2 rounded-full ${item.color}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Top Companies and Recent Activity */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Top Companies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Top Companies by Revenue</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {analytics.companies.map((company, index) => (
                <motion.div
                  key={company.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium mr-3">
                      {company.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{company.name}</p>
                      <p className="text-xs text-gray-500">
                        {company.plan.charAt(0).toUpperCase() + company.plan.slice(1)} • {company.users} users
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(company.revenue)}</p>
                    <div className="flex items-center">
                      {company.status === 'active' ? (
                        <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                      ) : (
                        <ClockIcon className="h-3 w-3 text-yellow-500 mr-1" />
                      )}
                      <span className={`text-xs ${
                        company.status === 'active' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {company.status}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {analytics.userActivity.map((activity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    activity.type === 'success' ? 'bg-green-500' :
                    activity.type === 'warning' ? 'bg-yellow-500' :
                    activity.type === 'error' ? 'bg-red-500' :
                    'bg-blue-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-500">{activity.company} • {activity.time}</p>
                  </div>
                  <div className="flex-shrink-0">
                    {activity.type === 'success' && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
                    {activity.type === 'warning' && <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />}
                    {activity.type === 'error' && <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />}
                    {activity.type === 'info' && <DocumentTextIcon className="h-4 w-4 text-blue-500" />}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* System Health */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="mt-8 bg-white shadow rounded-lg"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">System Health</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full mb-3">
                <ServerIcon className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="text-sm font-medium text-gray-900">Server Status</h4>
              <p className="text-xs text-green-600 mt-1">All systems operational</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full mb-3">
                <CloudArrowUpIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h4 className="text-sm font-medium text-gray-900">Storage Health</h4>
              <p className="text-xs text-blue-600 mt-1">68% capacity utilized</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full mb-3">
                <ChartBarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <h4 className="text-sm font-medium text-gray-900">Performance</h4>
              <p className="text-xs text-yellow-600 mt-1">Minor latency detected</p>
            </div>
          </div>
        </div>
      </motion.div>
      </div>
    </Layout>
  );
};

export default SystemAnalytics;